import { renderGunLab, itemAims } from './gunLab';
import { WEAPONS } from '../game/weapons';
import { SK } from './skin';

// F8 GUN-FIT OVERLAY (dev tool, owner ask): the F4 Guns sliders, but IN-GAME on a compact side panel so
// you SEE the equipped weapon while you drag. The view-lock row pins the camera + aim state (1st, 1st ADS,
// 3rd, 3rd AIM) so the pose you're tuning holds still; the muzzle marker stays lit so "flash on the barrel
// tip" is visually checkable. Slider changes apply live through the same weaponFit pipeline; Save writes
// weapon_fit.json exactly like the F4 tab.

export type ViewLock = 'free' | 'fp' | 'fpads' | 'tp' | 'tpaim';
const LOCKS: Array<[ViewLock, string]> = [['free', 'Free'], ['fp', '1st'], ['fpads', '1st ADS'], ['tp', '3rd'], ['tpaim', '3rd AIM']];

export class GunFitOverlay {
  open = false;
  lock: ViewLock = 'free';
  onOpenChange: ((open: boolean) => void) | null = null; // main frees/relocks the pointer + look here

  private readonly root: HTMLDivElement;
  private readonly gunTitle: HTMLDivElement;
  private readonly lockRow: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private gunId: string | null = null;

  constructor() {
    this.root = document.createElement('div');
    this.root.style.cssText = 'position:fixed;top:56px;right:12px;z-index:40;display:none;flex-direction:column;gap:8px;'
      + `width:370px;max-height:calc(100vh - 80px);overflow-y:auto;background:${SK.panel};border:1px solid ${SK.border};`
      + 'border-radius:10px;padding:12px 14px;font-family:ui-monospace,"SF Mono",Menlo,Consolas,monospace;color:#e8e2d8;'
      + 'box-shadow:0 12px 50px rgba(0,0,0,0.6)';
    this.root.dataset.ui = '1'; // presses here never register as fire triggers (input.ts syncPrimary gate)

    const head = document.createElement('div');
    head.style.cssText = 'display:flex;align-items:center;gap:8px';
    this.gunTitle = document.createElement('div');
    this.gunTitle.style.cssText = 'flex:1;font:700 14px ui-monospace,monospace;letter-spacing:1px';
    const hint = document.createElement('div');
    hint.textContent = 'F8 to close';
    hint.style.cssText = `font-size:10px;color:${SK.faint}`;
    head.append(this.gunTitle, hint);
    this.root.appendChild(head);

    this.lockRow = document.createElement('div');
    this.lockRow.style.cssText = 'display:flex;gap:5px';
    this.root.appendChild(this.lockRow);

    this.body = document.createElement('div');
    this.root.appendChild(this.body);
    document.body.appendChild(this.root);
    this.paintLocks();
  }

  toggle(): void { this.setOpen(!this.open); }

  setOpen(v: boolean): void {
    if (v === this.open) return;
    this.open = v;
    this.root.style.display = v ? 'flex' : 'none';
    if (!v) this.lock = 'free';
    this.paintLocks();
    if (v) this.paintBody();
    this.onOpenChange?.(v);
  }

  // Called each frame by main with the equipped weapon id ('ak' | WeaponId) or null. Re-renders the
  // sliders ONLY when the gun actually changes (rebuilding mid-drag would break the sliders).
  syncGun(id: string | null): void {
    if (!this.open || id === this.gunId) return;
    this.gunId = id;
    this.paintLocks(); // grey/ungrey the ADS/AIM buttons for items that have no aim pose
    this.paintBody();
  }

  private paintLocks(): void {
    this.lockRow.replaceChildren();
    const aims = this.gunId ? itemAims(this.gunId) : true;
    for (const [k, label] of LOCKS) {
      const b = document.createElement('button');
      b.textContent = label;
      const on = this.lock === k;
      // Non-aiming items (tools/melee/torch): the ADS/AIM views alias to hip/rest -- grey the buttons so
      // it's visible up front (they still work: the panel tunes the shared config + says so).
      const aliased = !aims && (k === 'fpads' || k === 'tpaim');
      b.style.cssText = `flex:1;padding:7px 0;border-radius:6px;cursor:pointer;font:700 11px ui-monospace,monospace;`
        + `border:1px solid ${on ? SK.borderSel : SK.border};background:${on ? 'rgba(207,197,180,0.16)' : SK.well};`
        + `color:${on ? '#f2eee6' : aliased ? 'rgba(150,142,130,0.45)' : SK.dim}`;
      b.addEventListener('click', () => { this.lock = k; this.paintLocks(); this.paintBody(); }); // re-filter sliders to this view's own fields
      this.lockRow.appendChild(b);
    }
  }

  private paintBody(): void {
    if (!this.gunId) {
      this.gunTitle.textContent = 'GUN FIT';
      this.body.replaceChildren();
      const msg = document.createElement('div');
      msg.textContent = 'Equip any item (hotbar) to tune it.';
      msg.style.cssText = `font-size:12px;color:${SK.dim};padding:10px 0`;
      this.body.appendChild(msg);
      return;
    }
    this.gunTitle.textContent = `GUN FIT — ${this.gunId === 'ak' ? 'AK' : WEAPONS[this.gunId as keyof typeof WEAPONS]?.name ?? this.gunId}`;
    // Filter sliders to the LOCKED view's own fields (owner 2026-07-12: 1P hip/1P ADS/3P rest/3P aim are
    // four independently-saved configs) -- 'free' shows every group, same as the F4 tab.
    renderGunLab(this.body, this.gunId, () => { /* live apply happens via onWeaponFitChange */ }, this.lock);
  }
}
